import { embedConsentAllowed } from './bd-embed-consent.js';
// Click-only video enhancement. Native watch links remain usable without JavaScript.
class BDVideoPlayer extends HTMLElement {
  static active = null;
  connectedCallback() {
    this.playButton = this.querySelector('[data-video-play]');
    this.closeButton = this.querySelector('[data-video-close]');
    this.mediaSlot = this.querySelector('[data-video-slot]');
    this.status = this.querySelector('[data-video-status]');
    this.onPlay = () => this.play();
    this.onClose = () => this.close(true);
    this.onKey = (event) => { if (event.key === 'Escape' && BDVideoPlayer.active === this) this.close(true); };
    this.onConsent = () => {
      if (this.dataset.kind !== 'hosted' && !this.allowed()) this.close(this.contains(document.activeElement));
    };
    this.playButton.addEventListener('click', this.onPlay);
    this.closeButton.addEventListener('click', this.onClose);
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('visitorConsentCollected', this.onConsent);
    document.addEventListener('customerPrivacyApiLoaded', this.onConsent);
    // A secondary entry can never repeat the main video or create a third player.
    if (this.dataset.role === 'secondary') {
      const main = document.querySelector('bd-video-player[data-role="main"]');
      const firstSecondary = document.querySelector('bd-video-player[data-role="secondary"]');
      if (firstSecondary !== this || (main && main.dataset.kind === this.dataset.kind && main.dataset.videoId === this.dataset.videoId)) {
        this.hidden = true;
        this.closest('.bd-team__video')?.setAttribute('hidden', '');
      }
    }
    const poster = this.querySelector('.bd-video-player__poster img');
    if (poster?.complete && !poster.naturalWidth) poster.remove();
    else poster?.addEventListener('error', (event) => event.target.remove(), {once:true});
  }
  allowed() { return embedConsentAllowed(); }

  play() {
    if (this.hidden) return;
    this.status.hidden = true;
    if (this.dataset.kind !== 'hosted' && !this.allowed()) {
      this.status.textContent = 'Your privacy preferences do not allow this embedded video. You can use the watch link below.';
      this.status.hidden = false;
      return;
    }
    let media;
    const id = this.dataset.videoId || '';
    if (this.dataset.kind === 'youtube' && /^[a-zA-Z0-9_-]{11}$/.test(id)) {
      media = document.createElement('iframe');
      media.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`;
    } else if (this.dataset.kind === 'vimeo' && /^\d+$/.test(id)) {
      media = document.createElement('iframe');
      media.src = `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1`;
    } else if (this.dataset.kind === 'hosted') {
      try {
        const source = new URL(this.dataset.source, location.href);
        if (source.protocol !== 'https:') throw new Error('Invalid video source');
        media = document.createElement('video');
        media.src = source.href;
        media.controls = true;
        media.playsInline = true;
        media.preload = 'none';
      } catch { /* The original watch link remains available. */ }
    }
    if (!media) {
      this.status.textContent = 'This video could not be loaded. Please use the watch link below.';
      this.status.hidden = false;
      return;
    }
    BDVideoPlayer.active?.close(false);
    BDVideoPlayer.active = this;
    if (media.tagName === 'IFRAME') {
      media.title = this.dataset.title || 'Better Deals video';
      media.allow = 'autoplay; encrypted-media; picture-in-picture';
      media.allowFullscreen = true;
      media.referrerPolicy = 'strict-origin-when-cross-origin';
    }
    media.addEventListener('error', () => {
      this.close(false);
      this.status.textContent = 'The video is unavailable here. Please use the watch link below.';
      this.status.hidden = false;
    }, {once:true});
    this.mediaSlot.replaceChildren(media);
    this.mediaSlot.hidden = false;
    this.playButton.hidden = true;
    this.closeButton.hidden = false;
    this.closeButton.focus();
    if (media.tagName === 'VIDEO') media.play().catch(() => { /* Native controls remain available. */ });
  }
  close(restoreFocus = false) {
    this.mediaSlot?.querySelector('video')?.pause();
    this.mediaSlot?.replaceChildren();
    if (this.mediaSlot) this.mediaSlot.hidden = true;
    if (this.playButton) this.playButton.hidden = false;
    if (this.closeButton) this.closeButton.hidden = true;
    if (BDVideoPlayer.active === this) BDVideoPlayer.active = null;
    if (restoreFocus && this.isConnected) this.playButton?.focus();
  }
  disconnectedCallback() {
    this.close(false);
    this.playButton?.removeEventListener('click', this.onPlay);
    this.closeButton?.removeEventListener('click', this.onClose);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('visitorConsentCollected', this.onConsent);
    document.removeEventListener('customerPrivacyApiLoaded', this.onConsent);
  }
}
if (!customElements.get('bd-video-player')) customElements.define('bd-video-player', BDVideoPlayer);
